import { forwardRef, useRef, useState } from 'react';
import {
  Divider,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';

import { COUNTRY, OTHER_COUNTRY } from '@/config/country';
import {
  findPhoneCountry,
  PHONE_COUNTRIES,
  type PhoneCountry,
} from '@/config/phoneCountries';
import {
  readMobilePhoneInput,
  type MobilePhoneValue,
} from '@/utils/phoneNumber';

const PANEL_COUNTRIES: PhoneCountry[] = [COUNTRY, OTHER_COUNTRY].map(
  (code) => findPhoneCountry(code)!
);

const REST_OF_COUNTRIES: PhoneCountry[] = PHONE_COUNTRIES.filter(
  (country) => !PANEL_COUNTRIES.includes(country)
);

const Flag = ({ country }: { country: PhoneCountry }) => (
  <img
    src={country.flag}
    alt=''
    className='h-4 w-6 shrink-0 rounded-[3px] object-cover ring-1 ring-black/10'
  />
);

export interface MobilePhoneInputProps {
  name: string;
  placeholder: string;
  value: MobilePhoneValue;
  onChange: (value: MobilePhoneValue) => void;
  onBlur?: () => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorInfo?: string;
  autoFocus?: boolean;
}

/**
 * Celular con selector de país. El prefijo internacional y el dígito fijo del
 * celular (el 9 de Uruguay y Perú) van como adorno; en el campo se tipean solo
 * los dígitos que siguen. Pegar un número con `+` cambia el país solo.
 */
const MobilePhoneInput = forwardRef<HTMLInputElement, MobilePhoneInputProps>(
  (
    {
      name,
      placeholder,
      value,
      onChange,
      onBlur,
      required = false,
      disabled = false,
      error = false,
      errorInfo,
      autoFocus = false,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const country = findPhoneCountry(value.country) ?? PANEL_COUNTRIES[0];
    const isMenuOpen = Boolean(menuAnchor);

    const setInputRef = (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === 'function') ref(element);
      else if (ref) ref.current = element;
    };

    const closeMenu = () => {
      setMenuAnchor(null);
      buttonRef.current?.focus();
    };

    const selectCountry = (code: string) => {
      setMenuAnchor(null);
      if (code !== country.code) onChange({ ...value, country: code });
      setTimeout(() => inputRef.current?.focus());
    };

    const renderOption = (option: PhoneCountry) => (
      <MenuItem
        key={option.code}
        selected={option.code === country.code}
        onClick={() => selectCountry(option.code)}
      >
        <ListItemIcon>
          <Flag country={option} />
        </ListItemIcon>
        <ListItemText primary={option.label} />
        <span className='ml-6 text-sm tabular-nums text-gray-500'>
          +{option.dialCode}
        </span>
      </MenuItem>
    );

    return (
      <>
        <TextField
          id={name}
          inputRef={setInputRef}
          label={placeholder}
          variant='outlined'
          value={value.number}
          onChange={(event) =>
            onChange(readMobilePhoneInput(event.target.value, value))
          }
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          error={error}
          autoFocus={autoFocus}
          helperText={error && errorInfo}
          autoComplete='off'
          fullWidth
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { inputMode: 'tel', autoComplete: 'off' },
            input: {
              startAdornment: (
                <InputAdornment position='start' sx={{ mr: 0.5 }}>
                  <button
                    ref={buttonRef}
                    type='button'
                    disabled={disabled}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    aria-haspopup='menu'
                    aria-expanded={isMenuOpen}
                    aria-label={`País del teléfono: ${country.label} (+${country.dialCode}). Cambiar país`}
                    className='-ml-1.5 flex cursor-pointer items-center gap-1.5 rounded-md py-1 pl-1.5 pr-0.5 text-gray-800 transition-colors hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent'
                  >
                    <Flag country={country} />
                    <span className='text-sm font-medium tabular-nums'>
                      +{country.dialCode}
                    </span>
                    <ArrowDropDownIcon
                      fontSize='small'
                      className={`text-gray-500 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <span
                    aria-hidden
                    className='mx-2 h-6 w-px shrink-0 bg-gray-300'
                  />
                  {country.mobilePrefix && (
                    <span className='text-base text-gray-500'>
                      {country.mobilePrefix}
                    </span>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
        <Menu
          anchorEl={menuAnchor}
          open={isMenuOpen}
          onClose={closeMenu}
          disableRestoreFocus
          slotProps={{ paper: { sx: { maxHeight: 380, minWidth: 260 } } }}
        >
          {PANEL_COUNTRIES.map(renderOption)}
          <Divider />
          {REST_OF_COUNTRIES.map(renderOption)}
        </Menu>
      </>
    );
  }
);

export default MobilePhoneInput;
